import { createSelector, createSlice } from '@reduxjs/toolkit';
import { filterOperatingPicture } from 'components/airSpace/airspaceUtils';
import { mapBoundingBoxSelector } from 'reducers/mapSlice';
import { filterMapFeaturesByBoundingBox } from 'utils/mapsUtils';
import { initialOperatingPictureState } from 'reducers/initialStates';

const operatingPictureSlice = createSlice({
	name: 'operatingPicture',
	initialState: {
		//Set by configureStore
	},
	reducers: {
		setOperatingPictureFeatures(state, action) {
			if (state.pagination.isInitialLoad) {
				// During initial load, append each page as it comes in
				state.features = [...state.features, ...action.payload];
			} else {
				// During refresh, collect features in temp array
				state.pagination.tempFeatures = [...state.pagination.tempFeatures, ...action.payload];
			}
		},
		setOperatingPictureTypesFilterValue(state, action) {
			state.typesFilterValue = action.payload;
		},
		setOperatingPictureSourcesFilterValue(state, action) {
			state.sourcesFilterValue = action.payload;
		},
		setOperatingPicturePagination(state, action) {
			state.pagination = {
				...state.pagination,
				...action.payload,
				tempFeatures: state.pagination.tempFeatures
			};
		},
		addOperatingPictureLoadedPage(state, action) {
			if (!state.pagination.loadedPages.includes(action.payload)) {
				state.pagination.loadedPages.push(action.payload);
			}
		},
		resetOperatingPictureState(state, action) {
			const isClosingPanel = action?.payload?.isClosingPanel;

			if (isClosingPanel) {
				// When closing panel, clear everything
				state.features = [];
				state.pagination = initialOperatingPictureState.pagination;
			} else {
				// During refresh cycles, keep features until we have new ones
				state.pagination = {
					...initialOperatingPictureState.pagination,
					isInitialLoad: false, // Set to false since this is a refresh
					tempFeatures: []
				};
			}
		},
		swapInNewFeatures(state) {
			state.features = state.pagination.tempFeatures;
			state.pagination.tempFeatures = [];
		},
		resetOperatingPictureSlice() {
			return initialOperatingPictureState;
		}
	}
});

export const {
	setOperatingPictureFeatures,
	setOperatingPictureTypesFilterValue,
	setOperatingPictureSourcesFilterValue,
	setOperatingPicturePagination,
	addOperatingPictureLoadedPage,
	resetOperatingPictureState,
	swapInNewFeatures,
	resetOperatingPictureSlice
} = operatingPictureSlice.actions;

export default operatingPictureSlice.reducer;

export const operatingPictureActions = Object.values(operatingPictureSlice.actions).map(
	action => action.type
);

// Selectors
export const operatingPictureFeaturesSelector = state => state.operatingPicture.features;
export const operatingPictureTypesFilterValueSelector = state =>
	state.operatingPicture.typesFilterValue;
export const operatingPictureSourcesFilterValueSelector = state =>
	state.operatingPicture.sourcesFilterValue;
export const operatingPicturePaginationSelector = state => state.operatingPicture.pagination;

// Features filtered by type/source only (no map bounds). Used internally as the base
// selector before applying map bounds; components should use operatingPictureSelector.
const operatingPictureFilteredWithoutBoundsSelector = createSelector(
	[
		operatingPictureFeaturesSelector,
		operatingPictureTypesFilterValueSelector,
		operatingPictureSourcesFilterValueSelector
	],
	(
		operatingPictureFeatures,
		operatingPictureTypesFilterValue,
		operatingPictureSourcesFilterValue
	) =>
		filterOperatingPicture(
			operatingPictureFeatures,
			operatingPictureTypesFilterValue,
			operatingPictureSourcesFilterValue
		)
);

export const operatingPictureSelector = createSelector(
	[operatingPictureFilteredWithoutBoundsSelector, mapBoundingBoxSelector],
	(filtered, mapBoundingBox) => {
		if (mapBoundingBox) {
			return filterMapFeaturesByBoundingBox({
				features: filtered,
				boundingBox: mapBoundingBox
			});
		}
		return filtered;
	}
);
